import { Navigate, createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { HotelAccommodation, hotelAccommodationSearchShape } from "../../components/accommodation-hotel/index";
import { NocAccommodation, nocAccommodationSearchShape } from "../../components/accommodation-noc/index";
import { AccommodationOverview } from "../../components/accommodation/overview";
import { PageSkeleton } from "../../components/accommodation/page-skeleton";
import { useGamesTimeZone } from "../../components/travel-leg-form";
import { accommodationView } from "../../lib/accommodation/access";
import { useDefaultGamesId } from "../../lib/competition/games";
import { isYmd, todayIn } from "../../lib/travel/view";
import { canSeeTravel } from "../../lib/travel/access";

/**
 * `/accommodation` — what it shows depends on who asks (`accommodationView`):
 *   host      the Overview (her `accOverview`), `day` = the night shown
 *   noc       `NocAccommodation` (S5, `components/accommodation-noc`)
 *   hotel     `HotelAccommodation` (S5, `components/accommodation-hotel`)
 *   helpdesk  an OPERATION group with only `helpdesk.*` → the Helpdesk tab
 * S5's views take no props: they read the session (`Route.useRouteContext`
 * of `/_app`) and the URL themselves.
 *
 * URL: `day` (the host Overview's night; games' zone, absent = today), plus
 * S5's own keys (`nocAccommodationSearchShape`, `hotelAccommodationSearchShape`).
 * Lenient: a hand-edited value falls back to "not set".
 */
export const Route = createFileRoute("/_app/accommodation/")({
	validateSearch: z.object({
		day: z
			.string()
			.regex(/^\d{4}-\d{2}-\d{2}$/)
			.optional()
			.catch(undefined),
		// S5's views keep their own URL state here (NOC: org state search order page page_size; hotel: view date building search page).
		...nocAccommodationSearchShape,
		...hotelAccommodationSearchShape,
	}),
	component: AccommodationIndex,
});

/** The one switch S5 plugs its views into. */
function AccommodationIndex() {
	const { session } = Route.useRouteContext();
	const view = accommodationView(session);
	if (view === "noc") return <NocAccommodation />;
	if (view === "hotel") return <HotelAccommodation />;
	if (view === "helpdesk") return <Navigate to="/accommodation/helpdesk" search={(s: { games_id?: string }) => ({ games_id: s.games_id })} replace />;
	return <HostOverview />;
}

function HostOverview() {
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const gamesId = useDefaultGamesId(search.games_id);
	const timeZone = useGamesTimeZone(gamesId);
	if (!gamesId || !timeZone) return <PageSkeleton />;
	const today = todayIn(timeZone);
	return (
		<AccommodationOverview
			gamesId={gamesId}
			gamesIdInUrl={search.games_id}
			timeZone={timeZone}
			today={today}
			deskLink={canSeeTravel(session) && session.active_role !== "ORGANIZATION"}
			date={isYmd(search.day) ? search.day : today}
			onDate={(d) => void navigate({ search: (s) => ({ ...s, day: d === today ? undefined : d }), replace: true })}
		/>
	);
}
